import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useAuth } from '@/contexts/auth.context';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useServiceStatus } from '@/contexts/service-status';
import { reportsService } from '@/services/reports.service';
import type {
  ReportsFilters,
  KPIDataResponse,
  StatusDistributionResponse,
  InspectionTypeDistributionResponse,
  TopProviderResponse,
  TopOperationalProviderResponse,
  TopCityResponse,
  MonthlyEvolutionResponse,
  CompletionTrendResponse,
  MonthlyStatusBreakdownResponse,
  YearComparisonResponse,
  RejectionRateResponse,
  OperationalRejectionRateResponse,
  ProviderComparisonResponse,
  OperationalProviderComparisonResponse,
  CityLocationResponse,
  InspectionLocationResponse,
  ProcessFunnelResponse,
  VolumeQualityResponse,
} from '@/services/reports.service';

export function useReportsData() {
  const { user } = useAuth();
  const { isProviderUser, isInspectorUser } = usePermissions();
  const { isServiceOnline } = useServiceStatus();

  // Verificar se o servico de inspection esta online
  const isInspectionServiceOnline = isServiceOnline('inspection');

  const [isLoadingKPIs, setIsLoadingKPIs] = useState(false);
  const [isLoadingCharts, setIsLoadingCharts] = useState(false);

  
  const [kpiData, setKpiData] = useState<KPIDataResponse | null>(null);
  const [statusDistribution, setStatusDistribution] = useState<StatusDistributionResponse[]>([]);
  const [inspectionTypeDistribution, setInspectionTypeDistribution] = useState<InspectionTypeDistributionResponse[]>([]);
  const [topProviders, setTopProviders] = useState<TopProviderResponse[]>([]);
  const [topOperationalProviders, setTopOperationalProviders] = useState<TopOperationalProviderResponse[]>([]);
  const [topCities, setTopCities] = useState<TopCityResponse[]>([]);
  const [monthlyEvolution, setMonthlyEvolution] = useState<MonthlyEvolutionResponse[]>([]);
  const [completionTrend, setCompletionTrend] = useState<CompletionTrendResponse[]>([]);
  const [monthlyStatusBreakdown, setMonthlyStatusBreakdown] = useState<MonthlyStatusBreakdownResponse[]>([]);
  const [yearComparison, setYearComparison] = useState<YearComparisonResponse[]>([]);
  const [rejectionRate, setRejectionRate] = useState<RejectionRateResponse[]>([]);
  const [operationalRejectionRate, setOperationalRejectionRate] = useState<OperationalRejectionRateResponse[]>([]);
  const [providerComparison, setProviderComparison] = useState<ProviderComparisonResponse[]>([]);
  const [operationalProviderComparison, setOperationalProviderComparison] = useState<OperationalProviderComparisonResponse[]>([]);
  const [cityLocations, setCityLocations] = useState<CityLocationResponse[]>([]);
  const [inspectionLocations, setInspectionLocations] = useState<InspectionLocationResponse[]>([]);
  const [processFunnel, setProcessFunnel] = useState<ProcessFunnelResponse[]>([]);
  const [volumeQuality, setVolumeQuality] = useState<VolumeQualityResponse[]>([]);

  
  const [activeFilters, setActiveFilters] = useState<ReportsFilters>({});

  
  const getEffectiveFilters = useCallback((): ReportsFilters => {
    const filters = { ...activeFilters };

    
    if (isProviderUser() && user?.providerId) {
      
      filters.providerId = user.providerId;

      
      
      
      
      

    }
    
    

    
    if (isInspectorUser() && user?.id) {
      filters.inspectorId = user.id;
    }

    
    

    return filters;
  }, [activeFilters, user, isProviderUser, isInspectorUser]);

  const loadKPIs = useCallback(async () => {
    // Nao fazer requisicao se servico offline
    if (!isInspectionServiceOnline) return;

    try {
      setIsLoadingKPIs(true);
      const filters = getEffectiveFilters();
      const data = await reportsService.getKPIs(filters);
      setKpiData(data);
    } catch {
      message.error('Erro ao carregar indicadores principais');
    } finally {
      setIsLoadingKPIs(false);
    }
  }, [getEffectiveFilters, isInspectionServiceOnline]);

  const loadOverviewCharts = useCallback(async () => {
    // Nao fazer requisicao se servico offline
    if (!isInspectionServiceOnline) return;

    try {
      setIsLoadingCharts(true);
      const filters = getEffectiveFilters();

      const [
        statusDist,
        inspectionTypeDist,
        providers,
        operationalProviders,
        cities
      ] = await Promise.all([
        reportsService.getStatusDistribution(filters),
        reportsService.getInspectionTypeDistribution(filters),
        reportsService.getTopProviders(filters),
        reportsService.getTopOperationalProviders(filters),
        reportsService.getTopCities(filters),
      ]);

      setStatusDistribution(statusDist);
      setInspectionTypeDistribution(inspectionTypeDist);
      setTopProviders(providers);
      setTopOperationalProviders(operationalProviders);
      setTopCities(cities);
    } catch {
      message.error('Erro ao carregar gráficos');
    } finally {
      setIsLoadingCharts(false);
    }
  }, [getEffectiveFilters, isInspectionServiceOnline]);


  const loadTemporalCharts = useCallback(async () => {
    // Nao fazer requisicao se servico offline
    if (!isInspectionServiceOnline) return;

    try {
      setIsLoadingCharts(true);
      const filters = getEffectiveFilters();

      const [
        evolution,
        trend,
        breakdown,
        comparison
      ] = await Promise.all([
        reportsService.getMonthlyEvolution(filters),
        reportsService.getCompletionTrend(filters),
        reportsService.getMonthlyStatusBreakdown(filters),
        reportsService.getYearComparison(filters),
      ]);

      setMonthlyEvolution(evolution);
      setCompletionTrend(trend);
      setMonthlyStatusBreakdown(breakdown);
      setYearComparison(comparison);
    } catch {
      message.error('Erro ao carregar análise temporal');
    } finally {
      setIsLoadingCharts(false);
    }
  }, [getEffectiveFilters, isInspectionServiceOnline]);

  const loadPerformanceCharts = useCallback(async () => {
    // Nao fazer requisicao se servico offline
    if (!isInspectionServiceOnline) return;

    try {
      setIsLoadingCharts(true);
      const filters = getEffectiveFilters();

      const [
        rejection,
        operationalRejection,
        comparison,
        operationalComparison
      ] = await Promise.all([
        reportsService.getRejectionRate(filters),
        reportsService.getOperationalRejectionRate(filters),
        reportsService.getProviderComparison(filters),
        reportsService.getOperationalProviderComparison(filters),
      ]);


      setRejectionRate(rejection);
      setOperationalRejectionRate(operationalRejection);
      setProviderComparison(comparison);
      setOperationalProviderComparison(operationalComparison);
    } catch {
      message.error('Erro ao carregar análise de performance');
    } finally {
      setIsLoadingCharts(false);
    }
  }, [getEffectiveFilters, isInspectionServiceOnline]);

  const loadGeographicCharts = useCallback(async () => {
    // Nao fazer requisicao se servico offline
    if (!isInspectionServiceOnline) return;

    try {
      setIsLoadingCharts(true);
      const filters = getEffectiveFilters();

      const [locations, inspections] = await Promise.all([
        reportsService.getCityLocations(filters),
        reportsService.getInspectionLocations(filters),
      ]);

      setCityLocations(locations);
      setInspectionLocations(inspections);
    } catch {
      message.error('Erro ao carregar mapa');
    } finally {
      setIsLoadingCharts(false);
    }
  }, [getEffectiveFilters, isInspectionServiceOnline]);

  const loadQualityCharts = useCallback(async () => {
    // Nao fazer requisicao se servico offline
    if (!isInspectionServiceOnline) return;

    try {
      setIsLoadingCharts(true);
      const filters = getEffectiveFilters();

      const [
        funnel,
        volQuality
      ] = await Promise.all([
        reportsService.getProcessFunnel(filters),
        reportsService.getVolumeQuality(filters),
      ]);

      setProcessFunnel(funnel);
      setVolumeQuality(volQuality);
    } catch {
      message.error('Erro ao carregar análise de qualidade');
    } finally {
      setIsLoadingCharts(false);
    }
  }, [getEffectiveFilters, isInspectionServiceOnline]);

  
  const refreshAllData = useCallback(() => {
    loadKPIs();
    loadOverviewCharts();
    loadTemporalCharts();
    loadPerformanceCharts();
    loadGeographicCharts();
    loadQualityCharts();
  }, [loadKPIs, loadOverviewCharts, loadTemporalCharts, loadPerformanceCharts, loadGeographicCharts, loadQualityCharts]);

  
  const handleFilterChange = useCallback((filters: ReportsFilters) => {
    setActiveFilters(filters);
  }, []);

  
  const handleClearFilters = useCallback(() => {
    setActiveFilters({});
  }, []);


  useEffect(() => {
    refreshAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters]); 

  return {
    
    isLoadingKPIs,
    isLoadingCharts,


    kpiData,
    statusDistribution,
    inspectionTypeDistribution,
    topProviders,
    topOperationalProviders,
    topCities,
    monthlyEvolution,
    completionTrend,
    monthlyStatusBreakdown,
    yearComparison,
    rejectionRate,
    operationalRejectionRate,
    providerComparison,
    operationalProviderComparison,
    cityLocations,
    inspectionLocations,
    processFunnel,
    volumeQuality,

    
    activeFilters,
    getEffectiveFilters,
    handleFilterChange,
    handleClearFilters,

    
    refreshAllData,
    loadKPIs,
    loadOverviewCharts,
    loadTemporalCharts,
    loadPerformanceCharts,
    loadGeographicCharts,
    loadQualityCharts,
  };
}
